import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  PlusCircle, 
  CheckCircle2, 
  Clock, 
  DollarSign, 
  Building2, 
  ShieldCheck, 
  Users, 
  Briefcase, 
  Award,
  Sparkles,
  Download,
  AlertCircle,
  Edit3,
  Trash2,
  Eye,
  Filter,
  ArrowRight,
  TrendingUp,
  Paperclip,
  Upload,
  ExternalLink,
  Link as LinkIcon
} from 'lucide-react';
import { GrantProposal, GrantBudgetItem, TrainerAllowanceClaim, FinancialTransaction } from '../../types';
import { getGrantProposalsDB, saveGrantProposalDB, deleteGrantProposalDB, getTrainerAllowancesDB, saveTrainerAllowanceDB, saveTransactionDB } from '../../services/db';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';
import { Modal } from '../../components/common/Modal';
import { SEO } from '../../components/common/SEO';
import { PageHeader } from '../../components/common/PageHeader';
import { isSuperAdmin, hasRolePermission } from '../../utils/formatters';

export const GrantManagementPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { showToast } = useToast();
  const [grants, setGrants] = useState<GrantProposal[]>([]);
  const [allowances, setAllowances] = useState<TrainerAllowanceClaim[]>([]);
  const [activeTab, setActiveTab] = useState<'proposals' | 'allowances' | 'eros'>('proposals');
  
  // Status filter for grants
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Modal State
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingGrantId, setEditingGrantId] = useState<string | null>(null);

  // Detail View Modal State
  const [viewingGrant, setViewingGrant] = useState<GrantProposal | null>(null);

  // Confirm Delete Modal State
  const [deletingGrantId, setDeletingGrantId] = useState<{ id: string; title: string } | null>(null);

  // Form State
  const [title, setTitle] = useState('');
  const [agencyName, setAgencyName] = useState('MOTAC');
  const [grantCategory, setGrantCategory] = useState<'Warisan & Kebudayaan' | 'Pembangunan Belia & Sukan' | 'Perpaduan Komuniti' | 'Infrastruktur Digital'>('Warisan & Kebudayaan');
  const [targetAmount, setTargetAmount] = useState(25000);
  const [approvedAmount, setApprovedAmount] = useState(25000);
  const [status, setStatus] = useState<'Draf Proposal' | 'Dalam Semakan Kementerian' | 'Diluluskan & Disalurkan' | 'Ditolak / Perlu Semakan'>('Draf Proposal');
  const [objective, setObjective] = useState('');
  const [targetAudience, setTargetAudience] = useState('');

  // Document Attachment State (Link or Local File Upload)
  const [attachmentMode, setAttachmentMode] = useState<'link' | 'file'>('link');
  const [documentUrl, setDocumentUrl] = useState('');
  const [documentFileName, setDocumentFileName] = useState('');

  // Allowance Form State
  const [isClaimModalOpen, setIsClaimModalOpen] = useState(false);
  const [programTitle, setProgramTitle] = useState('');
  const [trainerName, setTrainerName] = useState(currentUser?.name || '');
  const [trainerRole, setTrainerRole] = useState<'Ketua Jurulatih' | 'Pembantu Jurulatih' | 'Urusetia Program' | 'Penceramah Jemputan'>('Ketua Jurulatih');
  const [hours, setHours] = useState(6);
  const [rate, setRate] = useState(50);

  const isManagement = Boolean(currentUser && hasRolePermission(currentUser, ['Pengerusi', 'Timbalan Pengerusi', 'Setiausaha', 'Pen. Setiausaha', 'Bendahari', 'AJK']));

  useEffect(() => {
    getGrantProposalsDB().then(setGrants);
    getTrainerAllowancesDB().then(setAllowances);
  }, []);

  const handleOpenCreate = () => {
    setModalMode('create');
    setEditingGrantId(null);
    setTitle('');
    setAgencyName('MOTAC');
    setGrantCategory('Warisan & Kebudayaan');
    setTargetAmount(25000);
    setApprovedAmount(25000);
    setStatus('Draf Proposal');
    setObjective('');
    setTargetAudience('Belia & Remaja Sekolah (100 Peserta)');
    setDocumentUrl('');
    setDocumentFileName('');
    setAttachmentMode('link');
    setIsModalOpen(true);
  };

  const handleOpenEdit = (grant: GrantProposal) => {
    setModalMode('edit');
    setEditingGrantId(grant.id);
    setTitle(grant.title);
    setAgencyName(grant.agencyName);
    setGrantCategory(grant.grantCategory);
    setTargetAmount(grant.targetAmount);
    setApprovedAmount(grant.approvedAmount || grant.targetAmount);
    setStatus(grant.status);
    setObjective(grant.objective);
    setTargetAudience(grant.targetAudience);
    setDocumentUrl(grant.documentUrl || '');
    setDocumentFileName(grant.documentFileName || '');
    setAttachmentMode('link');
    setIsModalOpen(true);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setDocumentFileName(file.name);
      const reader = new FileReader();
      reader.onload = (event) => {
        const result = event.target?.result as string;
        setDocumentUrl(result);
        showToast('success', 'Fail Lampiran Dipilih!', file.name);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveGrant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !objective.trim()) {
      showToast('error', 'Sila isi tajuk dan objektif kertas kerja geran');
      return;
    }

    if (modalMode === 'create') {
      const newGrant: GrantProposal = {
        id: `grant-${Date.now()}`,
        title,
        agencyName,
        grantCategory,
        targetAmount: Number(targetAmount),
        approvedAmount: status === 'Diluluskan & Disalurkan' ? Number(approvedAmount) : undefined,
        status,
        submittedDate: new Date().toISOString().split('T')[0],
        contactPerson: `${currentUser?.name || 'Pentadbir'} (${currentUser?.role || 'AJK'})`,
        objective,
        targetAudience: targetAudience || 'Belia & Remaja Sekolah (100 Peserta)',
        budgetBreakdown: [
          { id: `b-${Date.now()}-1`, category: 'Elaun Kejurulatihan / Pengurusan', description: 'Elaun Sesi Latihan Jurulatih & Urusetia', estimatedCost: Math.round(targetAmount * 0.35) },
          { id: `b-${Date.now()}-2`, category: 'Perolehan Aset (CapEx)', description: 'Pembelian Kelengkapan Silat & Peralatan Mat Gelanggang NGO', estimatedCost: Math.round(targetAmount * 0.45) },
          { id: `b-${Date.now()}-3`, category: 'Penganjuran Modul & Bengkel', description: 'Penganjuran Bengkel Warisan, Dokumentasi & Sijil', estimatedCost: Math.round(targetAmount * 0.20) }
        ],
        erosFilingStatus: 'Lengkap (Annual Return Approved)',
        documentUrl: documentUrl.trim() || undefined,
        documentFileName: documentFileName.trim() || (documentUrl.trim() ? 'Dokumen_Geran.pdf' : undefined)
      };

      const updated = await saveGrantProposalDB(newGrant);
      setGrants(updated);

      // If approved directly on creation, log financial transaction to Turso DB
      if (status === 'Diluluskan & Disalurkan') {
        const tx: FinancialTransaction = {
          id: `tx-grant-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          category: 'Penerimaan Geran Kementerian',
          description: `Peruntukan Geran: ${title} (${agencyName})`,
          amount: Number(approvedAmount || targetAmount),
          type: 'Income',
          payerOrPayee: agencyName,
          gelanggang: 'Semua Gelanggang',
          recordedBy: currentUser?.name || 'Bendahari',
          status: 'Disahkan'
        };
        await saveTransactionDB(tx);
        showToast('success', 'Draf Geran Berjaya Dicipta!', `Dana RM ${Number(approvedAmount || targetAmount).toLocaleString('ms-MY')} direkodkan ke Buku Tunai Audit.`);
      } else {
        showToast('success', 'Draf Kertas Kerja Geran Disimpan!', `Tajuk: ${title}`);
      }
    } else if (modalMode === 'edit' && editingGrantId) {
      const existing = grants.find(g => g.id === editingGrantId);
      if (existing) {
        const isNewlyApproved = existing.status !== 'Diluluskan & Disalurkan' && status === 'Diluluskan & Disalurkan';
        const updatedGrant: GrantProposal = {
          ...existing,
          title,
          agencyName,
          grantCategory,
          targetAmount: Number(targetAmount),
          approvedAmount: status === 'Diluluskan & Disalurkan' ? Number(approvedAmount) : existing.approvedAmount,
          status,
          objective,
          targetAudience,
          documentUrl: documentUrl.trim() || existing.documentUrl,
          documentFileName: documentFileName.trim() || existing.documentFileName,
          budgetBreakdown: [
            { id: `b-${Date.now()}-1`, category: 'Elaun Kejurulatihan / Pengurusan', description: 'Elaun Sesi Latihan Jurulatih & Urusetia', estimatedCost: Math.round(targetAmount * 0.35) },
            { id: `b-${Date.now()}-2`, category: 'Perolehan Aset (CapEx)', description: 'Pembelian Kelengkapan Silat & Peralatan Mat Gelanggang NGO', estimatedCost: Math.round(targetAmount * 0.45) },
            { id: `b-${Date.now()}-3`, category: 'Penganjuran Modul & Bengkel', description: 'Penganjuran Bengkel Warisan, Dokumentasi & Sijil', estimatedCost: Math.round(targetAmount * 0.20) }
          ]
        };

        const updated = await saveGrantProposalDB(updatedGrant);
        setGrants(updated);

        if (isNewlyApproved) {
          const tx: FinancialTransaction = {
            id: `tx-grant-${Date.now()}`,
            date: new Date().toISOString().split('T')[0],
            category: 'Penerimaan Geran Kementerian',
            description: `Peruntukan Geran Diluluskan: ${title} (${agencyName})`,
            amount: Number(approvedAmount || targetAmount),
            type: 'Income',
            payerOrPayee: agencyName,
            gelanggang: 'Semua Gelanggang',
            recordedBy: currentUser?.name || 'Bendahari',
            status: 'Disahkan'
          };
          await saveTransactionDB(tx);
          showToast('success', 'Geran Diluluskan & Disalurkan!', `Dana RM ${Number(approvedAmount || targetAmount).toLocaleString('ms-MY')} kini dimasukkan ke Laporan Audit.`);
        } else {
          showToast('success', 'Maklumat Geran Dikemaskini!', `Status: ${status}`);
        }
      }
    }

    setIsModalOpen(false);
  };

  const handleUpdateGrantStatus = async (grantId: string, newStatus: 'Draf Proposal' | 'Dalam Semakan Kementerian' | 'Diluluskan & Disalurkan' | 'Ditolak / Perlu Semakan') => {
    const existing = grants.find(g => g.id === grantId);
    if (!existing) return;

    const isNewlyApproved = existing.status !== 'Diluluskan & Disalurkan' && newStatus === 'Diluluskan & Disalurkan';
    const updatedGrant: GrantProposal = {
      ...existing,
      status: newStatus,
      approvedAmount: newStatus === 'Diluluskan & Disalurkan' ? (existing.approvedAmount || existing.targetAmount) : existing.approvedAmount
    };

    const updated = await saveGrantProposalDB(updatedGrant);
    setGrants(updated);

    if (viewingGrant?.id === grantId) {
      setViewingGrant(updatedGrant);
    }

    if (isNewlyApproved) {
      const tx: FinancialTransaction = {
        id: `tx-grant-${Date.now()}`,
        date: new Date().toISOString().split('T')[0],
        category: 'Penerimaan Geran Kementerian',
        description: `Peruntukan Geran Diluluskan: ${existing.title} (${existing.agencyName})`,
        amount: existing.approvedAmount || existing.targetAmount,
        type: 'Income',
        payerOrPayee: existing.agencyName,
        gelanggang: 'Semua Gelanggang',
        recordedBy: currentUser?.name || 'Bendahari',
        status: 'Disahkan'
      };
      await saveTransactionDB(tx);
      showToast('success', 'Geran Diluluskan & Salurkan Dana!', `Dana RM ${(existing.approvedAmount || existing.targetAmount).toLocaleString('ms-MY')} dimasukkan ke Laporan Audit.`);
    } else {
      showToast('info', 'Status Kertas Kerja Geran Dikemaskini', `Status baharu: ${newStatus}`);
    }
  };

  const handleDeleteGrant = async (id: string) => {
    const updated = await deleteGrantProposalDB(id);
    setGrants(updated);
    if (viewingGrant?.id === id) {
      setViewingGrant(null);
    }
    setDeletingGrantId(null);
    showToast('info', 'Rekod Geran Telah Dipadam');
  };

  const handleCreateClaim = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!programTitle || !trainerName) {
      showToast('error', 'Sila isi nama program dan nama jurulatih');
      return;
    }

    const newClaim: TrainerAllowanceClaim = {
      id: `clm-${Date.now()}`,
      programTitle,
      trainerName,
      trainerRole,
      sessionDate: new Date().toISOString().split('T')[0],
      hours: Number(hours),
      allowanceRatePerHour: Number(rate),
      totalAllowance: Number(hours) * Number(rate),
      status: 'Diluluskan Bendahari'
    };

    const updated = await saveTrainerAllowanceDB(newClaim);
    setAllowances(updated);
    setIsClaimModalOpen(false);
    setProgramTitle('');
    showToast('success', 'Tuntutan Elaun Berjaya Dihantar!', `Jumlah: RM ${(hours * rate).toFixed(2)}`);
  };

  const totalApprovedGrants = grants
    .filter(g => g.status === 'Diluluskan & Disalurkan')
    .reduce((sum, g) => sum + (g.approvedAmount || g.targetAmount), 0);

  const totalAllowancesPaid = allowances
    .reduce((sum, a) => sum + a.totalAllowance, 0);

  const filteredGrants = grants.filter(g => {
    if (statusFilter === 'all') return true;
    return g.status === statusFilter;
  });

  if (!isSuperAdmin(currentUser)) {
    return (
      <div className="p-8 text-center text-slate-500 font-bold bg-white rounded-3xl border border-slate-200">
        Akses modul Pengurusan Geran terhad kepada Superadmin sahaja buat masa ini.
      </div>
    );
  }

  return (
    <div className="space-y-6 min-h-[75vh]">
      <SEO 
        title="Pengurusan Geran & Elaun | PSSASMM Kedah" 
        description="Portal Pengurusan Kertas Kerja Geran Kementerian, Monetisasi NGO Sah, Elaun Kejurulatihan, dan Semakan e-ROS Persatuan Seni Silat Awang Sulung Merah Muda."
      />

      {/* Standard PageHeader Component */}
      <PageHeader
        badgeText="Geran Kementerian & e-ROS"
        badgeIcon={ShieldCheck}
        title="Pengurusan Geran & Elaun NGO"
      >
        <button
          onClick={() => setIsClaimModalOpen(true)}
          className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold text-xs rounded-xl transition-all flex items-center gap-1.5 cursor-pointer border border-slate-200"
        >
          <PlusCircle className="w-4 h-4 text-slate-600" />
          <span>Tuntut Elaun</span>
        </button>
        {isManagement && (
          <button
            onClick={handleOpenCreate}
            className="px-4 py-2 bg-asmm-red hover:bg-asmm-crimson text-white font-bold text-xs rounded-xl shadow-sm transition-all flex items-center gap-1.5 cursor-pointer"
          >
            <PlusCircle className="w-4 h-4" />
            <span>Draf Geran Baru</span>
          </button>
        )}
      </PageHeader>

      {/* Summary KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500">Jumlah Geran Diluluskan</span>
            <Building2 className="w-5 h-5 text-emerald-600" />
          </div>
          <div className="text-xl font-black text-emerald-600 font-mono">
            RM {totalApprovedGrants.toLocaleString('ms-MY', { minimumFractionDigits: 2 })}
          </div>
          <div className="text-[10px] text-slate-400 font-medium">Disalurkan ke akaun rasmi NGO</div>
        </div>

        <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500">Elaun Jurulatih & Urusetia</span>
            <Users className="w-5 h-5 text-amber-600" />
          </div>
          <div className="text-xl font-black text-slate-900 font-mono">
            RM {totalAllowancesPaid.toLocaleString('ms-MY', { minimumFractionDigits: 2 })}
          </div>
          <div className="text-[10px] text-slate-400 font-medium">Manfaat kewangan sah penggerak</div>
        </div>

        <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500">Status Pematuhan e-ROS</span>
            <ShieldCheck className="w-5 h-5 text-asmm-red" />
          </div>
          <div className="text-sm font-black text-slate-900 flex items-center gap-1.5 mt-1">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            Laporan Tahunan Aktif
          </div>
          <div className="text-[10px] text-slate-400 font-medium">PPM-007-02-28082022 (Teratur)</div>
        </div>
      </div>

      {/* Main Tabs Navigation */}
      <div className="flex items-center gap-2 border-b border-slate-200 pb-2 overflow-x-auto no-scrollbar flex-nowrap">
        <button
          onClick={() => setActiveTab('proposals')}
          className={`px-4 py-2 rounded-xl font-extrabold text-xs transition-all cursor-pointer flex items-center gap-1.5 whitespace-nowrap shrink-0 ${
            activeTab === 'proposals'
              ? 'bg-asmm-red text-white shadow-sm'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <FileText className="w-4 h-4" />
          Kertas Kerja Geran ({grants.length})
        </button>
        <button
          onClick={() => setActiveTab('allowances')}
          className={`px-4 py-2 rounded-xl font-extrabold text-xs transition-all cursor-pointer flex items-center gap-1.5 whitespace-nowrap shrink-0 ${
            activeTab === 'allowances'
              ? 'bg-asmm-red text-white shadow-sm'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <Briefcase className="w-4 h-4" />
          Rekod Elaun Kejurulatihan ({allowances.length})
        </button>
        <button
          onClick={() => setActiveTab('eros')}
          className={`px-4 py-2 rounded-xl font-extrabold text-xs transition-all cursor-pointer flex items-center gap-1.5 whitespace-nowrap shrink-0 ${
            activeTab === 'eros'
              ? 'bg-asmm-red text-white shadow-sm'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <ShieldCheck className="w-4 h-4 text-amber-300" />
          Semakan e-ROS & Tatakelola
        </button>
      </div>

      {/* TAB 1: GRANT PROPOSALS CRUD */}
      {activeTab === 'proposals' && (
        <div className="space-y-4">
          {/* Status Filter Pill Buttons */}
          <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar flex-nowrap pb-1">
            <span className="text-xs font-bold text-slate-400 mr-1 flex items-center gap-1 shrink-0">
              <Filter className="w-3.5 h-3.5" /> Tapis:
            </span>
            {[
              { id: 'all', label: 'Semua Status', count: grants.length },
              { id: 'Draf Proposal', label: 'Draf', count: grants.filter(g => g.status === 'Draf Proposal').length },
              { id: 'Dalam Semakan Kementerian', label: 'Dalam Semakan', count: grants.filter(g => g.status === 'Dalam Semakan Kementerian').length },
              { id: 'Diluluskan & Disalurkan', label: 'Diluluskan', count: grants.filter(g => g.status === 'Diluluskan & Disalurkan').length },
              { id: 'Ditolak / Perlu Semakan', label: 'Ditolak', count: grants.filter(g => g.status === 'Ditolak / Perlu Semakan').length }
            ].map(f => (
              <button
                key={f.id}
                onClick={() => setStatusFilter(f.id)}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap shrink-0 border transition-all cursor-pointer flex items-center gap-1.5 ${
                  statusFilter === f.id
                    ? 'bg-asmm-red text-white border-asmm-red shadow-xs'
                    : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border-slate-200'
                }`}
              >
                <span>{f.label}</span>
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                  statusFilter === f.id ? 'bg-red-800 text-white' : 'bg-slate-200 text-slate-700'
                }`}>
                  {f.count}
                </span>
              </button>
            ))}
          </div>

          {/* Grants Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredGrants.map((grant) => (
              <div key={grant.id} className="p-5 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-4 hover:border-slate-300 transition-all relative flex flex-col justify-between">
                <div className="space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <span className="px-2.5 py-0.5 bg-slate-100 text-slate-700 text-[10px] font-black uppercase tracking-wider rounded-full border border-slate-200">
                        {grant.agencyName}
                      </span>
                      <h3 className="font-extrabold text-slate-900 text-sm mt-1.5 leading-snug">
                        {grant.title}
                      </h3>
                    </div>
                    <span className={`shrink-0 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider rounded-xl ${
                      grant.status === 'Diluluskan & Disalurkan'
                        ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                        : grant.status === 'Dalam Semakan Kementerian'
                        ? 'bg-amber-100 text-amber-800 border border-amber-300'
                        : grant.status === 'Ditolak / Perlu Semakan'
                        ? 'bg-rose-100 text-rose-800 border border-rose-300'
                        : 'bg-slate-100 text-slate-700 border border-slate-300'
                    }`}>
                      {grant.status}
                    </span>
                  </div>

                  <p className="text-xs text-slate-600 leading-relaxed font-medium line-clamp-2">
                    {grant.objective}
                  </p>

                  {/* Budget Allocations Preview */}
                  <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 space-y-2">
                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-wider">
                      PENGAGIHAN BELANJAWAN DIBADGETKAN:
                    </div>
                    {grant.budgetBreakdown.map((item) => (
                      <div key={item.id} className="flex items-center justify-between text-xs font-medium">
                        <span className="text-slate-700 font-semibold text-[11px] truncate max-w-[200px]">{item.category}</span>
                        <span className="font-mono font-bold text-slate-900 text-[11px]">
                          RM {item.estimatedCost.toLocaleString('ms-MY')}
                        </span>
                      </div>
                    ))}
                    <div className="pt-2 border-t border-slate-200 flex items-center justify-between font-black text-xs">
                      <span>JUMLAH PERMOHONAN:</span>
                      <span className="font-mono text-emerald-700 text-sm">
                        RM {grant.targetAmount.toLocaleString('ms-MY')}
                      </span>
                    </div>
                  </div>

                  {/* Lampiran Dokumen Kertas Kerja (Jika Ada) */}
                  {grant.documentUrl && (
                    <div className="pt-1">
                      <a
                        href={grant.documentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-asmm-red font-bold text-xs rounded-xl border border-red-200 transition-all group"
                      >
                        <Paperclip className="w-3.5 h-3.5" />
                        <span className="truncate max-w-[180px] sm:max-w-[240px]">
                          {grant.documentFileName || 'Dokumen_Kertas_Kerja.pdf'}
                        </span>
                        <ExternalLink className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                      </a>
                    </div>
                  )}
                </div>

                <div className="pt-3 border-t border-slate-100 flex items-center justify-between gap-2 mt-2">
                  <div className="text-[11px] text-slate-500 font-medium truncate">
                    <span>Pegawai: {grant.contactPerson}</span>
                  </div>

                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      onClick={() => setViewingGrant(grant)}
                      className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-lg text-xs font-bold transition-all flex items-center gap-1 cursor-pointer"
                      title="Lihat Perincian"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      <span>Lihat</span>
                    </button>

                    {isManagement && (
                      <>
                        <button
                          onClick={() => handleOpenEdit(grant)}
                          className="px-2.5 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-900 rounded-lg text-xs font-bold transition-all flex items-center gap-1 cursor-pointer border border-amber-200"
                          title="Sunting Kertas Kerja"
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                          <span>Sunting</span>
                        </button>
                        <button
                          onClick={() => setDeletingGrantId({ id: grant.id, title: grant.title })}
                          className="p-1.5 bg-rose-50 hover:bg-rose-100 text-rose-700 rounded-lg transition-all cursor-pointer border border-rose-200"
                          title="Padam"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB 2: TRAINER ALLOWANCES */}
      {activeTab === 'allowances' && (
        <div className="space-y-4">
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-extrabold text-slate-900 text-sm">
                  Log Tuntutan Elaun Pengurusan & Kejurulatihan
                </h3>
                <p className="text-xs text-slate-500 font-medium">
                  Elaun rasmi penggerak persatuan yang dipotong daripada bajet geran kementerian yang diluluskan.
                </p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse border border-slate-200 rounded-xl overflow-hidden">
                <thead>
                  <tr className="bg-slate-100 text-slate-900 font-extrabold text-[11px] border-b border-slate-300">
                    <th className="p-3 text-left">Program / Sesi</th>
                    <th className="p-3 text-left">Nama Jurulatih / Urusetia</th>
                    <th className="p-3 text-center">Peranan</th>
                    <th className="p-3 text-center">Jam / Sesi</th>
                    <th className="p-3 text-right">Kadar / Jam</th>
                    <th className="p-3 text-right">Jumlah Elaun (RM)</th>
                    <th className="p-3 text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {allowances.map((claim) => (
                    <tr key={claim.id} className="border-b border-slate-200 hover:bg-slate-50">
                      <td className="p-3 font-bold text-slate-900">{claim.programTitle}</td>
                      <td className="p-3 font-medium text-slate-800">{claim.trainerName}</td>
                      <td className="p-3 text-center">
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded-full font-bold text-[10px]">
                          {claim.trainerRole}
                        </span>
                      </td>
                      <td className="p-3 text-center font-mono font-bold">{claim.hours} Jam</td>
                      <td className="p-3 text-right font-mono">RM {claim.allowanceRatePerHour.toFixed(2)}</td>
                      <td className="p-3 text-right font-mono font-black text-emerald-700">
                        RM {claim.totalAllowance.toFixed(2)}
                      </td>
                      <td className="p-3 text-center">
                        <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded-full font-black text-[10px] uppercase">
                          {claim.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: e-ROS COMPLIANCE & GOVERNANCE */}
      {activeTab === 'eros' && (
        <div className="space-y-4">
          <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-4">
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
              <ShieldCheck className="w-8 h-8 text-asmm-red shrink-0" />
              <div>
                <h3 className="text-lg font-black tracking-wide uppercase text-slate-900">
                  Penyata Pematuhan e-ROS & Tatakelola Kewangan NGO
                </h3>
                <p className="text-xs text-slate-500 font-medium">
                  ROS No. Pendaftaran: <strong>PPM-007-02-28082022</strong> (Societies Act 1966)
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-2">
                <div className="flex items-center gap-2 font-extrabold text-xs text-emerald-700">
                  <CheckCircle2 className="w-4 h-4" />
                  Laporan Tahunan e-ROS
                </div>
                <p className="text-xs text-slate-600">
                  Penyata kewangan & minit mesyuarat agung tahunan (AGM) dihantar ke portal e-ROS tepat pada waktunya.
                </p>
              </div>

              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-2">
                <div className="flex items-center gap-2 font-extrabold text-xs text-emerald-700">
                  <CheckCircle2 className="w-4 h-4" />
                  Infrastruktur CRM Digital
                </div>
                <p className="text-xs text-slate-600">
                  Sistem pengurusan persatuan berasaskan web memberikan penilaian kredibiliti tinggi daripada panel kementerian.
                </p>
              </div>

              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-2">
                <div className="flex items-center gap-2 font-extrabold text-xs text-emerald-700">
                  <CheckCircle2 className="w-4 h-4" />
                  100% FPX Audit Trajectory
                </div>
                <p className="text-xs text-slate-600">
                  Transaksi kewangan direkodkan secara langsung melalui ToyyibPay FPX gateway dengan bukti audit bank.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DETAIL VIEW MODAL */}
      {viewingGrant && (
        <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 max-w-xl w-full space-y-5 border border-slate-200 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between border-b border-slate-200 pb-3 gap-3">
              <div>
                <span className="px-2.5 py-0.5 bg-slate-100 text-slate-700 text-[10px] font-black uppercase tracking-wider rounded-full border border-slate-200">
                  {viewingGrant.agencyName} • {viewingGrant.grantCategory}
                </span>
                <h3 className="font-extrabold text-slate-900 text-base mt-1.5">{viewingGrant.title}</h3>
              </div>
              <button onClick={() => setViewingGrant(null)} className="text-slate-400 hover:text-slate-600 font-black text-sm p-1">✕</button>
            </div>

            <div className="space-y-4 text-xs">
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-1">
                <div className="text-[10px] font-black text-slate-400 uppercase">Objektif Utama Kertas Kerja:</div>
                <p className="text-slate-800 leading-relaxed font-medium">{viewingGrant.objective}</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl">
                  <div className="text-[10px] font-black text-slate-400 uppercase">Sasaran Peserta:</div>
                  <div className="font-bold text-slate-900 mt-0.5">{viewingGrant.targetAudience}</div>
                </div>
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl">
                  <div className="text-[10px] font-black text-slate-400 uppercase">Pegawai Bertanggungjawab:</div>
                  <div className="font-bold text-slate-900 mt-0.5">{viewingGrant.contactPerson}</div>
                </div>
              </div>

              {/* Budget Items Breakdown */}
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-3">
                <div className="text-xs font-black uppercase text-asmm-red tracking-wider flex items-center justify-between border-b border-slate-200 pb-2">
                  <span>Pecahan Anggaran Belanjawan Geran</span>
                  <span className="font-mono text-slate-900 text-sm">RM {viewingGrant.targetAmount.toLocaleString('ms-MY')}</span>
                </div>
                <div className="space-y-2">
                  {viewingGrant.budgetBreakdown.map(b => (
                    <div key={b.id} className="p-2.5 bg-white border border-slate-200 rounded-xl flex items-center justify-between text-xs">
                      <div>
                        <div className="font-bold text-slate-900">{b.category}</div>
                        <div className="text-[10px] text-slate-500">{b.description}</div>
                      </div>
                      <div className="font-mono font-black text-emerald-700 shrink-0">
                        RM {b.estimatedCost.toLocaleString('ms-MY')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Document Attachment Download Box */}
              {viewingGrant.documentUrl && (
                <div className="p-4 bg-red-50/60 border border-red-200 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <div className="p-2 bg-asmm-red text-white rounded-xl">
                      <FileText className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="font-extrabold text-slate-900 text-xs">Fail / Pautan Dokumen Kertas Kerja</div>
                      <div className="text-[11px] text-slate-600 font-mono font-medium truncate max-w-[200px] sm:max-w-[280px]">
                        {viewingGrant.documentFileName || 'Dokumen_Kertas_Kerja.pdf'}
                      </div>
                    </div>
                  </div>

                  <a
                    href={viewingGrant.documentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3.5 py-2 bg-asmm-red hover:bg-asmm-crimson text-white font-bold text-xs rounded-xl shadow-xs transition-all flex items-center gap-1.5 shrink-0"
                  >
                    <Download className="w-3.5 h-3.5" />
                    <span>Muat Turun Dokumen</span>
                  </a>
                </div>
              )}

              {/* Status Update Actions for Management */}
              {isManagement && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl space-y-2">
                  <div className="text-xs font-extrabold text-amber-900">Kemaskini Status Geran Ini:</div>
                  <div className="flex flex-wrap gap-2 pt-1">
                    <button
                      onClick={() => handleUpdateGrantStatus(viewingGrant.id, 'Dalam Semakan Kementerian')}
                      className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold rounded-xl transition-all cursor-pointer shadow-xs"
                    >
                      Dalam Semakan
                    </button>
                    <button
                      onClick={() => handleUpdateGrantStatus(viewingGrant.id, 'Diluluskan & Disalurkan')}
                      className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl transition-all cursor-pointer shadow-xs"
                    >
                      Lulus & Salurkan Dana
                    </button>
                    <button
                      onClick={() => handleUpdateGrantStatus(viewingGrant.id, 'Ditolak / Perlu Semakan')}
                      className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-xl transition-all cursor-pointer shadow-xs"
                    >
                      Ditolak / Perlu Semakan
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="pt-2 flex justify-end gap-2 border-t border-slate-200">
              <button
                onClick={() => setViewingGrant(null)}
                className="px-4 py-2 bg-slate-100 text-slate-700 font-bold text-xs rounded-xl"
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CREATE / EDIT GRANT MODAL */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 max-w-lg w-full space-y-4 border border-slate-200 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-200 pb-3">
              <h3 className="font-extrabold text-slate-900 text-base">
                {modalMode === 'create' ? 'Draf Kertas Kerja Geran Kementerian' : 'Sunting Kertas Kerja Geran'}
              </h3>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600 font-black text-sm">✕</button>
            </div>

            <form onSubmit={handleSaveGrant} className="space-y-3">
              <div>
                <label className="text-xs font-extrabold text-slate-700 block mb-1">Tajuk Kertas Kerja / Program</label>
                <input
                  type="text"
                  required
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Contoh: Bengkel Pemeliharaan Warisan Seni Silat Melayu"
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs font-medium focus:ring-2 focus:ring-slate-900"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-extrabold text-slate-700 block mb-1">Agensi Sasaran</label>
                  <select
                    value={agencyName}
                    onChange={(e) => setAgencyName(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs font-bold"
                  >
                    <option value="MOTAC">MOTAC (Kebudayaan)</option>
                    <option value="KBS / JBSN Kedah">KBS / JBSN Kedah</option>
                    <option value="JPNIN (Perpaduan)">JPNIN (Perpaduan)</option>
                    <option value="YBM / Kerajaan Negeri">YBM / Kerajaan Negeri</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-extrabold text-slate-700 block mb-1">Status Kertas Kerja</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value as any)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs font-bold"
                  >
                    <option value="Draf Proposal">Draf Proposal</option>
                    <option value="Dalam Semakan Kementerian">Dalam Semakan Kementerian</option>
                    <option value="Diluluskan & Disalurkan">Diluluskan & Disalurkan</option>
                    <option value="Ditolak / Perlu Semakan">Ditolak / Perlu Semakan</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-extrabold text-slate-700 block mb-1">Jumlah Pohon (RM)</label>
                  <input
                    type="number"
                    required
                    value={targetAmount}
                    onChange={(e) => setTargetAmount(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs font-mono font-bold"
                  />
                </div>
                {status === 'Diluluskan & Disalurkan' && (
                  <div>
                    <label className="text-xs font-extrabold text-emerald-800 block mb-1">Jumlah Diluluskan (RM)</label>
                    <input
                      type="number"
                      required
                      value={approvedAmount}
                      onChange={(e) => setApprovedAmount(Number(e.target.value))}
                      className="w-full px-3 py-2 border border-emerald-300 rounded-xl text-xs font-mono font-black text-emerald-700 bg-emerald-50"
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="text-xs font-extrabold text-slate-700 block mb-1">Objektif Program</label>
                <textarea
                  rows={2}
                  required
                  value={objective}
                  onChange={(e) => setObjective(e.target.value)}
                  placeholder="Ringkasan objektif pemeliharaan warisan atau latihan..."
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs font-medium"
                />
              </div>

              <div>
                <label className="text-xs font-extrabold text-slate-700 block mb-1">Kumpulan Sasaran & Peserta</label>
                <input
                  type="text"
                  value={targetAudience}
                  onChange={(e) => setTargetAudience(e.target.value)}
                  placeholder="Contoh: 150 Belia B40 Gelanggang Wadi AnNour"
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs font-medium"
                />
              </div>

              {/* Dokumen Kertas Kerja (Pautan URL atau Muat Naik Fail) */}
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-2xl space-y-2.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-extrabold text-slate-900 flex items-center gap-1.5">
                    <Paperclip className="w-3.5 h-3.5 text-asmm-red" />
                    <span>Lampiran Dokumen Kertas Kerja (PDF/Word/Link)</span>
                  </label>
                  <div className="flex items-center gap-1 bg-white p-0.5 rounded-lg border border-slate-200">
                    <button
                      type="button"
                      onClick={() => setAttachmentMode('link')}
                      className={`px-2 py-0.5 text-[10px] font-bold rounded-md transition-all ${
                        attachmentMode === 'link' ? 'bg-asmm-red text-white' : 'text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      Pautan Link
                    </button>
                    <button
                      type="button"
                      onClick={() => setAttachmentMode('file')}
                      className={`px-2 py-0.5 text-[10px] font-bold rounded-md transition-all ${
                        attachmentMode === 'file' ? 'bg-asmm-red text-white' : 'text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      Muat Naik Fail
                    </button>
                  </div>
                </div>

                {attachmentMode === 'link' ? (
                  <div className="space-y-2">
                    <input
                      type="url"
                      value={documentUrl}
                      onChange={(e) => setDocumentUrl(e.target.value)}
                      placeholder="Pautan URL (Google Drive / PDF Link / Dropbox)..."
                      className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-mono font-medium"
                    />
                    <input
                      type="text"
                      value={documentFileName}
                      onChange={(e) => setDocumentFileName(e.target.value)}
                      placeholder="Nama Dokumen (cth: Kertas_Kerja_MOTAC_2025.pdf)..."
                      className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-medium"
                    />
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="relative border-2 border-dashed border-slate-300 rounded-xl p-3 bg-white hover:border-asmm-red transition-colors text-center cursor-pointer">
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                        onChange={handleFileUpload}
                        className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                      />
                      <div className="flex flex-col items-center justify-center gap-1 text-slate-500">
                        <Upload className="w-5 h-5 text-asmm-red" />
                        <span className="text-xs font-bold text-slate-700">
                          {documentFileName ? `Fail Dipilih: ${documentFileName}` : 'Tekan untuk muat naik fail PDF / Word'}
                        </span>
                        <span className="text-[10px] text-slate-400">PDF, DOCX, PNG disokong</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="pt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 bg-slate-100 text-slate-700 font-bold text-xs rounded-xl"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-asmm-red hover:bg-asmm-crimson text-white font-black text-xs rounded-xl shadow-md"
                >
                  {modalMode === 'create' ? 'Simpan Draf Geran' : 'Kemaskini Geran'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CONFIRM DELETE MODAL */}
      <Modal
        isOpen={!!deletingGrantId}
        onClose={() => setDeletingGrantId(null)}
        title="Sahkan Dipadam"
      >
        <div className="space-y-4">
          <p className="text-xs text-slate-600">
            Adakah anda pasti ingin memadam draf kertas kerja geran <strong>"{deletingGrantId?.title}"</strong>? Tindakan ini tidak boleh diundur.
          </p>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setDeletingGrantId(null)}
              className="px-4 py-2 bg-slate-100 text-slate-700 font-bold text-xs rounded-xl"
            >
              Batal
            </button>
            <button
              onClick={() => deletingGrantId && handleDeleteGrant(deletingGrantId.id)}
              className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs rounded-xl shadow-sm"
            >
              Padam Rekod
            </button>
          </div>
        </div>
      </Modal>

      {/* CREATE ALLOWANCE CLAIM MODAL */}
      {isClaimModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full space-y-4 border border-slate-200 shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 pb-3">
              <h3 className="font-extrabold text-slate-900 text-base">Borang Tuntutan Elaun Sesi</h3>
              <button onClick={() => setIsClaimModalOpen(false)} className="text-slate-400 hover:text-slate-600 font-black text-sm">✕</button>
            </div>

            <form onSubmit={handleCreateClaim} className="space-y-3">
              <div>
                <label className="text-xs font-extrabold text-slate-700 block mb-1">Nama Program / Bengkel</label>
                <input
                  type="text"
                  required
                  value={programTitle}
                  onChange={(e) => setProgramTitle(e.target.value)}
                  placeholder="Contoh: Sesi Latihan Silat Gelanggang Wadi AnNour"
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs font-medium"
                />
              </div>

              <div>
                <label className="text-xs font-extrabold text-slate-700 block mb-1">Nama Jurulatih / Urusetia</label>
                <input
                  type="text"
                  required
                  value={trainerName}
                  onChange={(e) => setTrainerName(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs font-bold"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-extrabold text-slate-700 block mb-1">Peranan</label>
                  <select
                    value={trainerRole}
                    onChange={(e) => setTrainerRole(e.target.value as any)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs font-bold"
                  >
                    <option value="Ketua Jurulatih">Ketua Jurulatih</option>
                    <option value="Pembantu Jurulatih">Pembantu Jurulatih</option>
                    <option value="Urusetia Program">Urusetia Program</option>
                    <option value="Penceramah Jemputan">Penceramah Jemputan</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-extrabold text-slate-700 block mb-1">Jam Bertugas</label>
                  <input
                    type="number"
                    required
                    value={hours}
                    onChange={(e) => setHours(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs font-mono font-bold"
                  />
                </div>
              </div>

              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center justify-between text-xs font-black text-emerald-900">
                <span>Jumlah Tuntutan:</span>
                <span className="font-mono text-sm">RM {(hours * rate).toFixed(2)}</span>
              </div>

              <div className="pt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsClaimModalOpen(false)}
                  className="px-4 py-2 bg-slate-100 text-slate-700 font-bold text-xs rounded-xl"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-emerald-600 text-white font-black text-xs rounded-xl shadow-md"
                >
                  Hantar Tuntutan
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
